var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import { signInWithEmail } from 'root/src/client/v2/auth/thunks/signInWithEmail';
import signUp from 'root/src/client/logic/auth/thunks/signUp';
import { authApi } from 'root/src/shared/util/authApi';
import getDomainRoute from 'root/src/client/util/getDomainRoute';
import { RESET_PASSWORD_ROUTE_ID } from 'root/src/shared/descriptions/routes/routeIds';
import { userGroups } from 'root/src/shared/constants/auth';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { Provider } from 'root/src/shared/@types/providers';
import { useSignInWithProvider } from 'root/src/client/web/auth/hooks/useSignInWithProvider';
import { PASSWORD_SCHEMA } from 'root/src/client/v2/auth/schemas/password.schema';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { FieldError } from 'root/src/client/v2/common/components/ui/field';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { GoogleIcon, TwitchIcon, TikTokIcon, DareDropLogo } from './signInIcons';
const SOCIAL_PROVIDERS = [Provider.Google, Provider.Twitch, Provider.TikTok];
const inputClassName = 'h-9 w-full rounded-lg border border-white/[0.06] bg-white/[0.03] px-3 text-sm '
    + 'placeholder:text-muted-foreground/30 transition-colors focus:border-primary/30 focus:outline-none';
const ACCOUNT_TYPES = [
    { value: userGroups.viewer, label: 'Viewer' },
    { value: userGroups.creator, label: 'Creator' },
    { value: userGroups.brand, label: 'Brand' },
];
const SocialButton = ({ provider, onSignedIn }) => {
    // Sign in via OIDC popup. On success the popup dispatches `cookiechange` (see useSignInWithProvider),
    // so `useAuthentication` picks up the new session reactively (navbar + queries refresh). We only close
    // the dialog here, no full page reload.
    const { onSignIn, providerLabel, isLoading } = useSignInWithProvider({
        provider,
        popup: true,
        popupOptions: {
            onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
                onSignedIn();
            }),
            onError: (error) => {
                toast.error('Could not sign in', { description: error.message });
            },
        },
    });
    return (<button type="button" onClick={onSignIn} disabled={isLoading} aria-label={`Sign in with ${providerLabel}`} className="flex h-9 flex-1 items-center justify-center rounded-lg border border-solid border-white/[0.06] bg-white/[0.04] text-sm transition-all hover:border-white/[0.12] hover:bg-white/[0.08] disabled:opacity-60">
			{isLoading
            ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-t-transparent"/>
            : (<>
						{provider === Provider.Google && <GoogleIcon className="h-4 w-4"/>}
						{provider === Provider.Twitch && <TwitchIcon className="h-4 w-4"/>}
						{provider === Provider.TikTok && <TikTokIcon className="h-4 w-4"/>}
					</>)}
		</button>);
};
export const SignInDialog = ({ children, open, onOpenChange, onSignIn, initialMode = 'signin' }) => {
    const dispatch = useAppDispatch();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [internalOpen, setInternalOpen] = useState(false);
    const [errorText, setErrorText] = useState('');
    const [mode, setMode] = useState(initialMode);
    const [forgotSent, setForgotSent] = useState(false);
    const [accountType, setAccountType] = useState(userGroups.creator);
    const { loading, success, error, run } = useAsyncAction();
    const isSignup = mode === 'signup';
    const isForgot = mode === 'forgot';
    const PasswordVisibilityIcon = showPassword ? VisibilityOffIcon : VisibilityIcon;
    const handleSubmit = (event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        event.preventDefault();
        setErrorText('');
        if (isSignup) {
            // Same bar the legacy signup form enforced (signUpSchema): password strength plus a
            // retyped confirmation, so a typo cannot silently become the account password.
            const passwordCheck = PASSWORD_SCHEMA.safeParse(password);
            if (!passwordCheck.success) {
                setErrorText((_b = (_a = passwordCheck.error.issues[0]) === null || _a === void 0 ? void 0 : _a.message) !== null && _b !== void 0 ? _b : 'Please choose a stronger password.');
                return;
            }
            if (password !== confirmPassword) {
                setErrorText('Passwords do not match.');
                return;
            }
        }
        try {
            yield run(() => __awaiter(void 0, void 0, void 0, function* () {
                if (isForgot) {
                    yield authApi.forgotPassword({ email, redirectUrl: getDomainRoute(RESET_PASSWORD_ROUTE_ID) });
                    setForgotSent(true);
                    toast.success('Reset link sent', { description: `Check ${email} for the link.` });
                }
                else if (isSignup) {
                    yield dispatch(signUp({ email, password, userGroup: accountType, redirectRoute: undefined, newsletterSub: false }));
                }
                else {
                    yield dispatch(signInWithEmail({ email, password, onSignIn }));
                    toast.success('Signed in', { description: 'Welcome back!' });
                }
            }), { onSuccess: mode === 'signin' ? () => setDialogOpen(false) : undefined });
        }
        catch (e) {
            const messages = e && typeof e === 'object' ? Object.values(e) : [];
            const fallback = isForgot
                ? 'Could not send the reset link. Please try again.'
                : isSignup ? 'Could not create your account. Please try again.' : 'Please check your email and password and try again.';
            const message = messages[0] || fallback;
            setErrorText(message);
            const errorTitle = isForgot ? 'Could not send reset link' : isSignup ? 'Could not sign up' : 'Could not sign in';
            toast.error(errorTitle, { description: message });
        }
    });
    const isControlled = open !== undefined;
    const dialogOpen = isControlled ? open : internalOpen;
    const setDialogOpen = (value) => {
        if (isControlled) {
            onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(value);
        }
        else {
            setInternalOpen(value);
        }
    };
    return (<DialogPrimitive.Root open={dialogOpen} onOpenChange={setDialogOpen}>
			{children && <DialogPrimitive.Trigger asChild>{children}</DialogPrimitive.Trigger>}
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-[1100] bg-black/80 data-[state=open]:animate-dialog-overlay-show data-[state=closed]:animate-dialog-overlay-hide"/>
				<DialogPrimitive.Content className="redesign fixed left-1/2 top-1/2 z-[1101] w-full -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl border border-white/[0.06] bg-card text-foreground shadow-2xl shadow-black/40 sm:max-w-[600px] data-[state=open]:animate-dialog-content-show data-[state=closed]:animate-dialog-content-hide">
					<DialogPrimitive.Title className="sr-only">Sign in</DialogPrimitive.Title>
					<DialogPrimitive.Description className="sr-only">Sign in to your Dare Drop account.</DialogPrimitive.Description>

					<div className="grid min-h-[480px] grid-cols-1 sm:grid-cols-[240px_1fr]">
						{/* Left branding panel */}
						<div className="relative hidden flex-col justify-between overflow-hidden rounded-l-2xl p-6 sm:flex" style={{ background: 'linear-gradient(160deg, var(--grad-brand-from) 0%, hsl(262 83% 40%) 40%, hsl(200 80% 35%) 70%, var(--grad-accent-from) 100%)' }}>
							<div className="absolute right-4 top-8 h-16 w-16 rounded-full border border-white/[0.15] opacity-40"/>
							<div className="absolute bottom-20 left-3 h-10 w-10 rotate-12 rounded-lg border border-white/[0.12] opacity-30"/>
							<div className="absolute right-8 top-1/2 h-6 w-6 rounded-full bg-white/[0.08]"/>
							<div className="absolute bottom-32 right-6 h-px w-20 rotate-45 bg-white/[0.15]"/>

							<div>
								<div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.15] backdrop-blur-sm">
									<DareDropLogo className="h-5 w-5 text-white"/>
								</div>
								<h3 className="mt-5 font-display text-lg font-bold leading-tight text-white">Dare Drop</h3>
								<p className="mt-2 text-[13px] leading-relaxed text-white/60">
									Discover game drops, support creators, connect with publishers.
								</p>
							</div>

							<p className="text-[10px] uppercase tracking-wider text-white/30">Dare to discover</p>
						</div>

						{/* Right form panel */}
						<div className="flex flex-col">
							<div className="flex flex-1 flex-col justify-center p-6 sm:p-7">
								<AnimatePresence mode="wait">
									<motion.div key={mode} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2, ease: 'easeOut' }}>
										<h2 className="font-display text-lg font-semibold">{isForgot ? 'Reset password' : isSignup ? 'Create your account' : 'Welcome back'}</h2>
										<p className="mt-1 text-xs text-muted-foreground/60">{isForgot ? 'Enter your email and we will send a reset link.' : isSignup ? 'Start your journey with Dare Drop.' : 'Sign in to your account.'}</p>

										<div className="mt-5 space-y-4">
											{isForgot && forgotSent ? (<div className="rounded-lg border border-solid border-neon-green/20 bg-neon-green/10 p-4 text-xs leading-relaxed text-neon-green">
													Check your inbox. We sent a password reset link to <span className="font-medium">{email}</span>.
												</div>) : (<>
													{!isForgot && (<>
															<div className="flex gap-2">
																{SOCIAL_PROVIDERS.map(provider => (<SocialButton key={provider} provider={provider} onSignedIn={() => setDialogOpen(false)}/>))}
															</div>

															<div className="relative">
																<div className="absolute inset-0 flex items-center">
																	<div className="w-full border-t border-white/[0.06]"/>
																</div>
																<div className="relative flex justify-center text-[10px]">
																	<span className="bg-card px-2 text-muted-foreground/40">email</span>
																</div>
															</div>
														</>)}

													<form onSubmit={handleSubmit} className="space-y-2.5">
														<input type="email" required placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} className={inputClassName}/>
														{!isForgot && (<div className="relative">
																<input type={showPassword ? 'text' : 'password'} required placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className={cn(inputClassName, 'pr-9')}/>
																<button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/25 transition-colors hover:text-muted-foreground" aria-label={showPassword ? 'Hide password' : 'Show password'}>
																	<PasswordVisibilityIcon sx={{ fontSize: 14 }}/>
																</button>
															</div>)}

														{isSignup && (<input type={showPassword ? 'text' : 'password'} required placeholder="Confirm password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={inputClassName}/>)}

														{isSignup && (<div>
																<span className="mb-1.5 block text-[10px] uppercase tracking-wider text-muted-foreground/40">I am a</span>
																<div className="flex gap-1 rounded-lg border border-solid border-white/[0.06] bg-white/[0.03] p-1">
																	{ACCOUNT_TYPES.map((type) => {
                    const active = accountType === type.value;
                    return (<button key={type.value} type="button" onClick={() => setAccountType(type.value)} className={cn('relative flex-1 rounded-lg py-2 text-xs font-medium transition-colors', active ? 'text-neon-green' : 'text-muted-foreground hover:text-foreground')}>
																				{active && (<motion.span layoutId="signup-account-type" className="absolute inset-0 rounded-lg border border-solid border-neon-green/50 bg-neon-green/10" transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}/>)}
																				<span className="relative z-10">{type.label}</span>
																			</button>);
                })}
																</div>
															</div>)}

														{mode === 'signin' && (<div className="flex justify-end">
																<button type="button" onClick={() => { setErrorText(''); setMode('forgot'); }} className="text-[11px] text-primary/60 transition-colors hover:text-primary">
																	Forgot password?
																</button>
															</div>)}

														<FieldError>{errorText}</FieldError>

														<Button type="submit" variant="brand" loading={loading} success={success} error={error} className="h-9 w-full rounded-lg text-sm font-medium">
															{isForgot ? 'Send reset link' : isSignup ? 'Create Account' : 'Sign In'}
														</Button>
													</form>
												</>)}
										</div>
									</motion.div>
								</AnimatePresence>
							</div>

							<div className="border-t border-white/[0.04] px-6 py-3">
								<p className="text-center text-xs text-muted-foreground/40">
									{isForgot ? 'Remember your password?' : isSignup ? 'Already have an account?' : 'New to Dare Drop?'}{' '}
									<button type="button" onClick={() => { setErrorText(''); setForgotSent(false); setConfirmPassword(''); setMode(mode === 'signin' ? 'signup' : 'signin'); }} className="font-medium text-primary/70 transition-colors hover:text-primary">
										{isForgot ? 'Back to sign in' : isSignup ? 'Sign in' : 'Sign up free'}
									</button>
								</p>
							</div>
						</div>
					</div>

					<DialogPrimitive.Close className="absolute right-4 top-4 z-30 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-muted-foreground focus:ring-offset-2">
						<CloseIcon sx={{ fontSize: 18 }}/>
						<span className="sr-only">Close</span>
					</DialogPrimitive.Close>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>);
};
